import {
    DatePicker,
    IconNames,
    Input,
} from '@triumph/design-system';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { BillingReminder } from '../billingReminder.types';
import { CopyToClipboardButton } from '@/app/shared/components/ClipboardCopy/ClipboardCopyButton';

interface PendingInvoicesTableColumnProps {
    handleNotesChange: (invoiceNumber: string, includeDisputedReasons: boolean) => void
    handlePaymentDateChange: (invoiceNumber: string, selectedPaymentDate: string) => void
}

export const pendingInvoicesTableColumns = ({
    handlePaymentDateChange,
    handleNotesChange
}: PendingInvoicesTableColumnProps) => {

    const { t } = useTranslation('billingReminder', { keyPrefix: 'table' });

    return [
    {
        order: 1,
        header: t('invoiceNumber'),
        field: 'invoiceNumber',
        sortable: false
    },
    {
        order: 2,
        header: t('referenceNumber'),
        field: 'referenceNumber',
        sortable: false,
        dynamicColumnFormatter: (data: any, row: BillingReminder) => {
            return (
                <CopyToClipboardButton value={row.referenceNumber} />
            )
        }
    },
    {
        order: 3,
        header: t('carrierName'),
        field: 'carrierName',
        sortable: false
    },
    {
        order: 4,
        header: t('amount'),
        field: 'amount',
        sortable: false
    },
    {
        order: 5,
        header: t('amountDue'),
        field: 'amountDue',
        sortable: false
    },
    {
        order: 6,
        header: t('invoiceDate'),
        field: 'invoiceDate',
        sortable: false
    },
    {
        order: 7,
        header: t('invoiceDueDate'),
        field: 'invoiceDueDate',
        sortable: false
    },
    {
        order: 8,
        header: t('paymentDate'),
        field: 'paymentDate',
        sortable: false,
        dynamicColumnFormatter: (data: any, row: BillingReminder) => {
            return (
                <div>
                    <DatePicker
                        key={`datepicker-${row.invoiceId}-${row.paymentDate ?? 'empty'}`}
                        id={`paydate.datepicker.${row.invoiceId}`}
                        onChange={(selectedPaymentDate) => {
                            const date = Array.isArray(selectedPaymentDate)
                                ? selectedPaymentDate[0]
                                : selectedPaymentDate;
                            if (date) {
                                handlePaymentDateChange(row.invoiceNumber, date.toISOString().split('T')[0]);
                            }
                        }}
                        allowRange={false}
                        defaultValue={(row.paymentDate ? new Date(row.paymentDate + 'T00:00:00') : undefined)}
                        startDate={(row.paymentDate ? new Date(row.paymentDate + 'T00:00:00') : undefined)}>
                    </DatePicker>
                </div>
            )
        }
    },
    {
        order: 9,
        header: '',
        field: '',
        sortable: false,
        dynamicColumnFormatter: () => {
            return (
                <div>
                    <b>OR</b>
                </div>
            )
        }
    },
    {
        order: 10,
        header: t('issueWithInvoice'),
        field: 'reason',
        sortable: false,
        dynamicColumnFormatter: (data: any, row: BillingReminder) => {
            return (
                <div>
                    <Input
                        key={`reason-${row.invoiceId}`}
                        id={`reason-input-${row.invoiceId}`}
                        value={row.reason ?? ""}
                        rightIcon={{
                            iconName: IconNames.Interface_List,
                            show: true
                        }}
                        iconAction={() => handleNotesChange(row.invoiceNumber, true)}
                        onFocus={() => handleNotesChange(row.invoiceNumber, true)}
                    />
                </div>
            )
        }
    },
    {
        order: 11,
        header: "Add Note (Optional)",
        field: 'additionalInformationButton',
        sortable: false,
        dynamicColumnFormatter: (data: any, row: BillingReminder) => {
            return (
                <div>
                    <Input
                        key={`userText-${row.invoiceId}`}
                        id={`userText-input-${row.invoiceId}`}
                        value={row.userText ?? ""}
                        rightIcon={{
                            iconName: IconNames.Messages_Comment,
                            show: true
                        }}
                        iconAction={() => handleNotesChange(row.invoiceNumber, false)}
                        onFocus={() => handleNotesChange(row.invoiceNumber, false)}
                    />
                </div>
            )
        }
    },
]
}
